import React, { useState } from 'react';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  const services = [
    {
      id: 1,
      title: 'AI Chatbots & Virtual Assistants',
      description: 'Intelligent conversational AI that enhances customer engagement and automates support.',
      timeline: '4-8 weeks',
      image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'Advanced AI chatbots powered by GPT and custom ML models. We create intelligent virtual assistants that understand context, learn from interactions, and provide human-like conversations.'
    },
    {
      id: 2,
      title: 'AI Automation & Workflow',
      description: 'Streamline operations with intelligent automation that saves time and reduces costs.',
      timeline: '6-12 weeks',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'End-to-end AI automation solutions using machine learning and RPA. Automate repetitive tasks, process documents intelligently, and optimize workflows.'
    },
    {
      id: 3,
      title: 'AI Calling & Voice Solutions',
      description: 'Revolutionary voice AI systems for sales, support, and customer engagement.',
      timeline: '6-10 weeks',
      image: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'Cutting-edge AI calling systems with natural voice synthesis and recognition. Perfect for outbound sales and appointment scheduling.'
    },
    {
      id: 4,
      title: 'Machine Learning & Predictive Analytics',
      description: 'Data-driven insights and predictions to make smarter business decisions.',
      timeline: '8-14 weeks',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'Custom machine learning models that predict trends, customer behavior, and business outcomes.'
    },
    {
      id: 5,
      title: 'Computer Vision & Image Recognition',
      description: 'Advanced visual AI for object detection, facial recognition, and image analysis.',
      timeline: '8-16 weeks',
      image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'State-of-the-art computer vision solutions for surveillance, quality control, medical imaging, and retail analytics.'
    },
    {
      id: 6,
      title: 'Natural Language Processing',
      description: 'Extract insights from text data with advanced NLP and text analytics.',
      timeline: '6-12 weeks',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      about: 'Advanced NLP solutions for text classification, sentiment analysis, and entity extraction.'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-orange-400 font-semibold mb-3">The AION WISE Advantage</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Premium AI Services</h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Discover the cutting-edge AI solutions that make industry leaders trust us with their most critical projects
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden hover:border-orange-500 transition-all duration-300 group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                <p className="text-gray-400 mb-4">{service.description}</p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-orange-400 font-medium">{service.timeline}</span>
                </div>

                {expandedService === service.id && (
                  <div className="space-y-4 mb-4 border-t border-gray-700 pt-4">
                    <div>
                      <h4 className="text-white font-semibold mb-2">About This Service</h4>
                      <p className="text-gray-400 text-sm">{service.about}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-orange-500 text-orange-400 hover:bg-orange-500/10"
                    onClick={() => toggleService(service.id)}
                  >
                    {expandedService === service.id ? (
                      <>
                        Show Less <ChevronUp className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Learn More <ChevronDown className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                  <Button
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Book Demo
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500/10"
            onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
          >
            View All Services
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
